import { faAngleRight as lightAngleRight } from "@jewlr/pro-light-svg-icons/faAngleRight"
import Button from "@jewlr/storybook/core/button"
import { FloatInput } from "@jewlr/storybook/form"
import PropTypes from "prop-types"
import React from "react"
import { useForm, Controller } from "react-hook-form"
import { connect } from "react-redux"
import styled, { withTheme } from "styled-components"

import { getShipEstimate, updateShipEstView } from "areas/product/store/actions"
import { Box, Flex, Typography } from "components/common/index"
import FontAwesomeIcon from "components/font-awesome-icon"
import { IS_NA, IS_US, jewlr_t, IS_JEWLR, IS_PORTAL } from "helpers/application"

const ContentHeading = styled(Flex).attrs((props) => ({
  alignItems: "center",
  flexDirection: { _: "column", tablet: "row" },
  ...props,
}))`
  background-color: ${(props) => props.theme.shipModal.heading.background};
  margin-bottom: 15px;
  padding: 10px 20px;
  ${(props) => props.theme.mediaQueries.tablet`
    margin-bottom: 10px;
    padding: 10px 20px;
  `}
`

const HeadingText = styled.span`
  color: ${(props) => props.theme.colors.black};
  font-size: 15px;
  letter-spacing: 0.5px;
  line-height: 21px;
  margin: 0 20px 0 0;
  ${(props) => props.theme.mediaQueries.tablet`
    display: none;
  `}
`

const HeadingTitle = styled(Box).attrs((props) => ({
  as: "h4",
  ...props,
}))`
  color: ${(props) => props.theme.colors.black};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin: 0 20px 0 0;
`

const HeadingLink = styled(Button).attrs((props) => ({
  variant: "text",
  ...props,
}))`
  align-items: flex-end;
  color: ${(props) => props.theme.colors.link};
  font-size: 15px;
  letter-spacing: 0.5px;
`

const GuaranteedDelivery = styled.div`
  color: ${(props) => props.theme.colors.guaranteed};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-top: 20px;
  text-align: center;
  ${(props) => props.theme.mediaQueries.tablet`
    text-align: left;
  `}
`

const IntroText = styled(Box)`
  font-size: 15px;
  letter-spacing: 0.5px;
  line-height: 21px;
  margin: 0 0 20px;
  text-align: center;
  white-space: pre-wrap;
  ${(props) => props.theme.mediaQueries.tablet`
    text-align: left;
  `}
`

const SubmitButton = styled(Button).attrs((props) => ({
  bg: props.theme.shipModal.button.background,
  color: "white",
  display: "block",
  my: { _: 2, tablet: 0 },
  width: "auto",
  ...props,
}))`
  ${(props) => props.theme.mediaQueries.tablet`
    text-transform: uppercase;
  `}
`

const Table = styled.table`
  margin-bottom: 40px;
  width: 100%;
  ${(props) => props.theme.mediaQueries.tablet`
    margin-bottom: 20px;
    max-width: 400px;
  `}
  thead {
    border-bottom: 1px solid ${(props) => props.theme.colors.grey10};
  }
  tbody {
    vertical-align: top;
  }
  th,
  td {
    font-size: 15px;
    letter-spacing: 0.5px;
    padding: 5px 5px 5px 0;
    text-align: left;
  }
  th {
    color: ${(props) => props.theme.colors.grey35};
    ${(props) => props.theme.mediaQueries.mobileOnly`
      font-weight: 500;
    `}
  }
  td {
    color: ${(props) => props.theme.colors.grey65};
  }
  th:last-child,
  td:last-child {
    text-align: right;
  }
`

const Th = styled(Box).attrs((props) => ({
  as: "th",
  ...props,
}))``

const NoWrap = styled.span`
  white-space: nowrap;
`

const BlueText = styled.span`
  color: #4268ff;
`

const PostalCodeText = styled(Typography)`
  color: ${(props) => props.theme.colors.black};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  line-height: 21px;
  margin: 0 20px 0 0;
  ${(props) => props.theme.mediaQueries.tablet`
    display: none;
  `}
`

const GreenText = styled.span`
  color: #20a41c;
`

const NextText = styled(Typography)`
  font-size: 15px;
  line-height: 21px;
  white-space: pre-wrap;
`

const ShippingMethod = styled(Box)`
  ${(props) => props.theme.mediaQueries.mobileOnly`
    display: flex;
    flex-direction: column;
  `}
`

const ShipForm = ({ ship, submit }) => {
  const is_us = IS_US()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      country: ship.hidden_form.country,
      pkg: ship.hidden_form.pkg,
      po_box: ship.po_checked ? ship.po_checked : "",
      portal: IS_PORTAL(),
      postal_code: ship.postal_code,
      ship_date: ship.hidden_form.ship_date,
      shipper: ship.hidden_form.shipper,
      style_code: ship.hidden_form.style_code,
    },
    mode: "onBlur",
    resolver: async (values) => {
      const errors = {}
      const pattern = new RegExp(ship.zip_pattern)

      if (!values.postal_code) {
        errors.postal_code =
          "Please enter your " +
          jewlr_t("address.zip").toLowerCase() +
          (is_us ? " code" : "")
      } else if (
        !pattern.test(
          is_us ? (values.postal_code || "").trim() : values.postal_code
        )
      ) {
        errors.postal_code = "Invalid format"
      }

      return {
        errors,
        values,
      }
    },
  })

  const onSubmit = async (values) => {
    try {
      if (!values.po_box) {
        delete values.po_box
      }
      await new Promise((res, rej) => {
        submit(values, {
          onError: (err) => rej(err),
          onSuccess: () => res(),
        })
      })
    } catch (err) {
      setError("root.error", {
        message: err?.errors?.[0] || err?.message || err,
      })
    }
  }

  return (
    <div>
      {false && sl.config.show_guaranteed && (
        <GuaranteedDelivery>
          ALL ITEMS ARE GUARANTEED FOR DELIVERY BEFORE CHRISTMAS
        </GuaranteedDelivery>
      )}
      <IntroText>
        To find out when you will receive your item, enter your{" "}
        {jewlr_t("address.zip").toLowerCase() + (is_us ? " code" : "")} below to
        view <NoWrap>your delivery options.</NoWrap>
      </IntroText>
      <ContentHeading display={{ _: "none", tablet: "flex" }}>
        <HeadingTitle>When will I receive my order?</HeadingTitle>
      </ContentHeading>
      <Box mb="25px" mt="10px">
        <form onSubmit={handleSubmit(onSubmit)}>
          {errors?.root?.error && (
            <Typography color="error" mb={1}>
              {errors.root.error.message}
            </Typography>
          )}
          <Controller
            control={control}
            name="postal_code"
            render={({ field, fieldState: { error } }) => (
              <FloatInput
                autoComplete="postal-code"
                data-cy="postal-code-field"
                error={error}
                label={
                  "Enter your " +
                  jewlr_t("address.zip").toLowerCase() +
                  (is_us ? " code" : "") +
                  " here"
                }
                mb="8px"
                p="0"
                type="text"
                {...field}
              />
            )}
          />
          <Flex
            alignItems={{ _: "center", tablet: "flex-start" }}
            flexDirection="column"
            width="100%"
          >
            <Controller
              control={control}
              name="po_box"
              render={({ field }) => (
                <Box
                  alignItems="center"
                  as="label"
                  display="inline-flex"
                  mb={2}
                  mt={{ _: 2, tablet: 0 }}
                >
                  <Box
                    as="input"
                    data-cy="delivery-dates-input"
                    defaultChecked={!!ship.po_checked}
                    mr={1}
                    type="checkbox"
                    verticalAlign="middle"
                    {...field}
                  />
                  This is a PO Box
                </Box>
              )}
            />
            <SubmitButton
              data-cy="getting-delivery-dates"
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              {isSubmitting ? "Getting Dates..." : "Get Delivery Dates"}
            </SubmitButton>
          </Flex>
        </form>
      </Box>
    </div>
  )
}
ShipForm.propTypes = {
  ship: PropTypes.object,
  submit: PropTypes.func,
  theme: PropTypes.object,
}

const ResultTable = ({ changeView, ship, onNext }) => {
  return (
    <div>
      <IntroText display={{ _: "block", tablet: "flex" }}>
        <div>
          Every {IS_PORTAL() ? "Safyre Labs" : sl.config.site_title} item is
          custom made just for you.{" "}
        </div>
        <div>
          The receive date(s) includes custom manufacturing{" "}
          <NoWrap>time and delivery.</NoWrap>
        </div>
      </IntroText>
      <ContentHeading>
        <HeadingTitle display={{ _: "none", tablet: "block" }}>
          Delivery Dates to <BlueText>{ship.postal_code}</BlueText>
        </HeadingTitle>
        <HeadingText>Delivery Dates to:</HeadingText>
        <PostalCodeText>{ship.postal_code}</PostalCodeText>
        {IS_NA() && (
          <HeadingLink onClick={() => changeView("FORM")} underline>
            Change{" "}
            {jewlr_t("address.zip").toLowerCase() + (IS_US() ? " code" : "")}
            <FontAwesomeIcon icon={lightAngleRight} ml="4px" />
          </HeadingLink>
        )}
      </ContentHeading>

      <Table data-cy="shipping-dates-table">
        <thead>
          <tr>
            <th>Shipping Method</th>
            <Th width={{ _: "35%", tablet: "auto" }}>Receive Date</Th>
            <Th width={{ _: "15%", tablet: "auto" }}>Cost</Th>
          </tr>
        </thead>
        <tbody>
          {ship.methods
            .filter((method) => method.code !== "pickup")
            .map((method, idx) => (
              <tr key={`shipTable-${method.label}`}>
                <td data-cy={`shipping-method-${idx}`}>
                  <ShippingMethod>
                    <span>
                      {method.charge_text.toLowerCase() == "free" && (
                        <strong>Free </strong>
                      )}
                      {method.label}
                    </span>
                    {method.carrier && (
                      <NoWrap>{` (${method.carrier})`}</NoWrap>
                    )}
                  </ShippingMethod>
                </td>
                <td>{method.receive_dates}</td>
                <td>
                  {method.charge_text.toLowerCase() == "free" ? (
                    <GreenText>Free</GreenText>
                  ) : (
                    method.charge_text
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      {IS_JEWLR() && sl.config.enable_gift_preview_video && (
        <Flex
          alignItems="center"
          flexDirection={{ _: "column", tablet: "row" }}
        >
          <NextText>Need something sooner? </NextText>
          <HeadingLink
            onClick={(e) => {
              if (onNext) {
                e.preventDefault()
                onNext()
              }
            }}
          >
            We have a solution
            <FontAwesomeIcon icon={lightAngleRight} ml="4px" />
          </HeadingLink>
        </Flex>
      )}
    </div>
  )
}

ResultTable.propTypes = {
  changeView: PropTypes.func,
  onNext: PropTypes.func,
  ship: PropTypes.object,
}
const InternalShipPop = ({ changeView, ship, submit, theme, onNext }) => {
  if (!ship) {
    return null
  }

  if (ship.view_type == "OUT_OF_STOCK") {
    return (
      <Box>
        We&apos;re sorry, this item is currently unavailable. Please check back
        soon!
      </Box>
    )
  }
  return (
    <Flex flexDirection="column" overflow="hidden">
      <div>
        {IS_NA() && ship.view_type === "FORM" && !!ship.hidden_form && (
          <ShipForm ship={ship} submit={submit} theme={theme} />
        )}
        {ship.view_type === "RESULTS" && (
          <ResultTable changeView={changeView} onNext={onNext} ship={ship} />
        )}
      </div>
      {!ship.view_type && (
        <Typography>
          Order now and it will ship by <strong>{ship.esd}</strong>.
        </Typography>
      )}
    </Flex>
  )
}

InternalShipPop.propTypes = {
  changeView: PropTypes.func,
  onNext: PropTypes.func,
  ship: PropTypes.object,
  submit: PropTypes.func,
  theme: PropTypes.object,
}

const mapStateToProps = (state) => {
  return {
    ship: state.wizard.shipping ? state.wizard.shipping.estimator : null,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    changeView: (view) => {
      dispatch(updateShipEstView(view))
    },
    submit: (values, actions) => {
      dispatch(getShipEstimate(values, actions))
    },
  }
}

const ShipPop = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTheme(InternalShipPop))

export default ShipPop
